// THE ? SHEET — every live keyboard shortcut, grouped (HOME_AMBIENT_SKIN_SPEC
// §5 "Sheet" · DESIGN-WL-SHELL-KEYBOARD-MOTION-1).
//
// Built from the D2 kit, nothing hand-rolled: AppDialog v2 as a CENTRE sheet,
// AppDialogHead "Keyboard" (its Close is the brand CloseButton, square, through
// the brand ModalHeader), AppMicroLabel group heads, AppKeycap keys. Weights
// 500 / 700 only (the head's title is the kit's 700; everything here is 500).
//
// THE ROWS are the registry's (functions/shortcuts.js `shortcutSheetGroups`):
// only rows whose KEYSTROKE works right now — the person's single-key switch
// and the workspace flag are honoured, because a keycap for a dead key is a
// lie (AppKeycap's own rule). The command palette (ws-w2) lists the same rows
// through `listShortcuts`.
//
// OPEN STATE: the AppDialog `stateKey` convention, on `root._shortcutsSheetOpen`
// — written by `?` (`openShortcutsSheet`) or by the palette's row, cleared by
// the dialog's own close paths (Esc, the veil, the head's Close). The key's
// declaration in state.js (STATE_DEFAULTS + WORKSPACE_UNSCOPED_KEYS) is handed
// to the state.js owner as docs/patches/shortcuts-state-key.patch; the sheet's
// mount test proves it opens on a root that does NOT declare the key.
//
// MOUNT: once, at the shell root (app.js — ws-w2's file), handed over as
// docs/patches/shortcuts-overlay-mount.patch. Shell-level, so the root is
// `fixed` to the viewport (AppDialog's `absolute` is for a module frame) and
// sits in the Modal.js band (DESIGN_SPEC §6.8: over PageSlot 1300, under the
// dropdown and modal roots).
export const ShortcutsOverlay = {
  extends: 'AppDialog',
  stateKey: '_shortcutsSheetOpen',
  presentation: 'center',
  panelSize: 'md',
  initialFocus: 'panel',
  position: 'fixed',
  zIndex: '2000',

  Panel: {
    AppDialogHead: {
      heading: 'shell.shortcuts.title',
      titleId: 'shellShortcutsTitle'
    },
    AppDialogBody: {
      ShortcutsSheetGroups: {
        flow: 'y',
        gap: 'B',
        childExtends: 'ShortcutsSheetGroup',
        childrenAs: 'state',
        children: (el) => el.call('shortcutSheetGroups')
      }
    }
  }
}

// One group: its micro-label head, then its rows.
export const ShortcutsSheetGroup = {
  flow: 'y',
  gap: 'Y',
  AppMicroLabel: {
    text: (el, s) => el.call('polyglot', s.label || '')
  },
  ShortcutsSheetRows: {
    flow: 'y',
    childExtends: 'ShortcutsSheetRow',
    childrenAs: 'state',
    children: (el, s) => Array.from(s.rows || [])
  }
}

// One row: what the key does, then the keys. A sequence reads "G then H", a
// range "1 – 9"; the key legends are the registry's (Latin, as printed). The
// row declares its tag: it is keyed by its shortcut id, and a key that is an
// HTML tag name becomes the tag (the ⌘K row, id `search`, was a <search>
// landmark).
export const ShortcutsSheetRow = {
  tag: 'div',
  flow: 'x',
  align: 'center space-between',
  gap: 'A',
  padding: 'Y2 0',
  minWidth: '0',
  ShortcutsRowLabel: {
    tag: 'span',
    minWidth: '0',
    fontSize: 'Z1',
    fontWeight: '500',
    color: 'title',
    text: (el, s) => el.call('polyglot', s.label || '')
  },
  ShortcutsRowKeys: {
    flow: 'x',
    align: 'center',
    gap: 'X',
    flexShrink: '0',
    children: (el, s) => {
      const caps = Array.from(s.keycaps || [])
      const out = []
      caps.forEach((cap, i) => {
        if (i && s.joiner === 'then') {
          out.push({
            key: 'then' + i,
            extends: 'ShortcutsSheetJoiner',
            text: '{{ shell.shortcuts.then | polyglot }}'
          })
        }
        if (i && s.joiner === 'range') {
          out.push({ key: 'range' + i, extends: 'ShortcutsSheetJoiner', text: '–' })
        }
        out.push({ key: 'cap' + i, extends: 'AppKeycap', text: String(cap) })
      })
      return out
    }
  }
}

// The word or glyph between two keys.
export const ShortcutsSheetJoiner = {
  tag: 'span',
  fontSize: 'Y',
  fontWeight: '500',
  color: 'caption'
}
